// LunaRequestsCard — Home "Active Luna Requests" list.
// design ref: home.jsx LunaRequestsCard + LunaRequestRow, styles.css:263–303
// (.card--luna / .luna-row).
//
// Behaviour (per product): the card is shown only when there are active Luna
// requests. With none, it renders nothing at all (no empty state) — the home
// just collapses to the cards that have content.

import { Box, Flex, HStack, Icon } from "@chakra-ui/react";
import { format } from "date-fns";
import { TbAlertCircleFilled, TbArrowRight } from "react-icons/tb";

import Card from "components/ds/Card";
import CardHeader from "components/ds/CardHeader";
import { LunaMark } from "components/ds/LunaMark";
import type { LunaRequest } from "services/luna/lunaApi";

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : format(d, "EEE, dd MMM");
};

const LunaRow = ({
  request,
  onRespond,
}: {
  request: LunaRequest;
  onRespond: () => void;
}) => (
  // design ref: styles.css:270–303 — .luna-row. Rendered values reflect the
  // `task-row` class it also carries inside `.inner-table`: padding 12px 16px,
  // gap 16px (verified via computed styles on the design prototype).
  <Flex
    align="center"
    gap="lg" // 16px
    px="lg" // 16px
    py="md" // 12px
    borderRadius="component.lg"
    cursor="pointer"
    transition="background-color 120ms"
    _hover={{ bg: "bg.hover" }}
    onClick={onRespond}
    _notLast={{
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderBottomColor: "neutral.100",
    }}
  >
    <LunaMark size={18} animated={false} />
    <Box
      flex="1"
      minW={0}
      fontSize="md"
      fontWeight={500}
      color="neutral.950"
      isTruncated
    >
      {request.title || "New request"}
    </Box>
    {/* design ref: styles.css:283–303 — .luna-row__right */}
    <HStack
      spacing="sm"
      flexShrink={0}
      fontSize="smd"
      color="neutral.700"
      flexWrap="wrap"
    >
      <HStack spacing="1xs" color="warning.800" fontWeight={500}>
        <Icon as={TbAlertCircleFilled} boxSize="16px" color="warning.500" />
        Active
      </HStack>
      <Box as="span" color="neutral.400">
        ·
      </Box>
      <Box as="span">{formatDate(request.updatedAt)}</Box>
      <Box as="span" color="neutral.400">
        ·
      </Box>
      <HStack
        as="button"
        type="button"
        spacing="2xs"
        color="p.600"
        fontWeight={600}
        _hover={{ color: "p.700" }}
        onClick={(e) => {
          e.stopPropagation();
          onRespond();
        }}
      >
        Respond
        <Icon as={TbArrowRight} boxSize="14px" />
      </HStack>
    </HStack>
  </Flex>
);

export interface LunaRequestsCardProps {
  requests: LunaRequest[];
  onViewAll: () => void;
  onRespond: (sessionId: string) => void;
}

/**
 * The requests this card would show: the ones the member still has to answer.
 *
 * Exported because Home lays its grid out around whether this card renders, and
 * a second copy of the predicate there could drift from this one.
 */
export const activeLunaRequests = (requests: LunaRequest[]): LunaRequest[] =>
  requests.filter((r) => r.status !== "closed");

const LunaRequestsCard = ({
  requests,
  onViewAll,
  onRespond,
}: LunaRequestsCardProps) => {
  const active = activeLunaRequests(requests);

  // No active requests → the card doesn't show at all.
  if (active.length === 0) return null;

  return (
    <Card variant="luna">
      <CardHeader
        title={
          <HStack spacing="sm" align="center" as="span" display="inline-flex">
            <LunaMark size={18} animated={false} />
            <Box as="span">Active Luna Requests</Box>
          </HStack>
        }
        count={active.length}
        action={{ label: "View all", onClick: onViewAll }}
      />
      {/* design ref: styles.css:222–228 — .inner-table holds the rows. */}
      <Box
        bg="neutral.25"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="neutral.200"
        borderRadius="component.2xl"
        overflow="hidden"
        p="sm"
        display="flex"
        flexDirection="column"
        gap="sm"
      >
        {active.slice(0, 5).map((r) => (
          <LunaRow
            key={r.sessionId}
            request={r}
            onRespond={() => onRespond(r.sessionId)}
          />
        ))}
      </Box>
    </Card>
  );
};

export default LunaRequestsCard;
